import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const WHATSAPP_NUMBER = '917870369666';
const WHATSAPP_MESSAGE = "Hi! I'd like to book a table at Scratch Unleashed 🎉";
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`;

// Hide on:
//   - admin / account dashboards
//   - standalone ticket page
//   - any event-detail or checkout flow that uses its own sticky bottom CTA
const HIDDEN_PREFIXES = ['/admin', '/account', '/tickets'];
// Routes that match these regexes also hide the float (sub-routes only)
const HIDDEN_REGEXES = [/^\/events\/[^/]+/];  // /events/:slug and /events/:slug/checkout

export default function WhatsAppFloat() {
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  const isHidden =
    HIDDEN_PREFIXES.some(p => location.pathname.startsWith(p)) ||
    HIDDEN_REGEXES.some(rx => rx.test(location.pathname));

  // Delay appearance slightly for a polished feel
  useEffect(() => {
    if (isHidden) { setVisible(false); return; }
    const t = setTimeout(() => setVisible(true), 1200);
    return () => clearTimeout(t);
  }, [isHidden]);

  // Show tooltip once on first visit
  useEffect(() => {
    if (!visible) return;
    const seen = sessionStorage.getItem('wa_tooltip_seen');
    if (!seen) {
      setShowTooltip(true);
      sessionStorage.setItem('wa_tooltip_seen', '1');
      const t = setTimeout(() => setShowTooltip(false), 4000);
      return () => clearTimeout(t);
    }
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-6 right-6 z-50 flex items-center gap-3"
      style={{ pointerEvents: 'none' }}
    >
      {/* Tooltip bubble */}
      {showTooltip && (
        <div
          className="bg-[#1A1A1A] text-[#F2F2F2] text-sm px-4 py-2 rounded-2xl shadow-xl border border-[rgba(255,255,255,0.08)] whitespace-nowrap animate-fade-in"
          style={{
            pointerEvents: 'auto',
            animation: 'fadeInLeft 0.3s ease',
          }}
        >
          💬 Book via WhatsApp
        </div>
      )}

      {/* WhatsApp FAB */}
      <a
        href={WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat on WhatsApp to book a table at Scratch Unleashed"
        data-testid="whatsapp-float-button"
        onClick={() => setShowTooltip(false)}
        style={{
          pointerEvents: 'auto',
          width: 56,
          height: 56,
          borderRadius: '50%',
          background: '#25D366',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 20px rgba(37,211,102,0.45)',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          textDecoration: 'none',
          flexShrink: 0,
        }}
        onMouseEnter={e => {
          e.currentTarget.style.transform = 'scale(1.12)';
          e.currentTarget.style.boxShadow = '0 6px 28px rgba(37,211,102,0.6)';
          setShowTooltip(true);
        }}
        onMouseLeave={e => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = '0 4px 20px rgba(37,211,102,0.45)';
        }}
      >
        {/* WhatsApp SVG icon */}
        <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
        </svg>
      </a>
    </div>
  );
}
